import { z } from "zod";
import { useTranslation } from "react-i18next";
import { UseFormReturn } from "react-hook-form";
import { optionalFiles, optionalFilesXlsxOnly } from "@/utils/form";
import { FileUploadComponent } from "@/components/shared/form";
import { TFunction } from "i18next";
import { Card } from "@/components/ui/card";
import { SUPPORTED_FILE_TYPES_STRING } from "@/constants/documents";

export const createDocumentSchema = (t: TFunction) => {
  return z.object({
    document: z.object({
      publication: optionalFiles(),
      serviceDescription: optionalFiles(),
      contract: optionalFiles(),
      otherDocuments: optionalFiles(),
      priceForms: optionalFilesXlsxOnly()
    }).check((ctx) => {
      const totalFiles = [
        ...(ctx.value.publication || []),
        ...(ctx.value.serviceDescription || []),
        ...(ctx.value.contract || []),
        ...(ctx.value.otherDocuments || []),
        ...(ctx.value.priceForms || [])
      ].length;

      if (totalFiles > 10) {
        ctx.issues.push({
          code: "custom",
          message: t("schemas:createtender.document.maxfileserror"),
          path: ["maxerror"],
          input: totalFiles,
        });
      }
    }),
  });
};

export const documentDefaultValues = {
  document: {
    publication: [],
    serviceDescription: [],
    contract: [],
    otherDocuments: [],
    priceForms: []
  }
};


export function DocumentFormGroup({ form }: { form: UseFormReturn<any> }) {
  const { t } = useTranslation();

  const formFields = [
    {
      name: "document.publication",
      label: t("newtender.formgroups.document.inputs.publication.label"),
      description: t("newtender.formgroups.document.inputdescription", { fileTypes: SUPPORTED_FILE_TYPES_STRING }),
      article: t("newtender.formgroups.document.inputs.publication.article"),
      showOptional: true
    },
    {
      name: "document.serviceDescription",
      label: t("newtender.formgroups.document.inputs.servicedescription.label"),
      description: t("newtender.formgroups.document.inputdescription", { fileTypes: SUPPORTED_FILE_TYPES_STRING }),
      article: t("newtender.formgroups.document.inputs.servicedescription.article"),
      showOptional: true
    },
    {
      name: "document.contract",
      label: t("newtender.formgroups.document.inputs.contract.label"),
      description: t("newtender.formgroups.document.inputdescription", { fileTypes: SUPPORTED_FILE_TYPES_STRING }),
      article: t("newtender.formgroups.document.inputs.contract.article"),
      showOptional: true
    },
    {
      name: "document.priceForms",
      label: t("newtender.formgroups.document.inputs.priceform.label"),
      description: t("newtender.formgroups.document.inputdescription", { fileTypes: "XLSX" }),
      article: t("newtender.formgroups.document.inputs.priceform.article"),
      showOptional: true,
      accept: {
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      },
    },
    {
      name: "document.otherDocuments",
      label: t("newtender.formgroups.document.inputs.other.label"),
      description: t("newtender.formgroups.document.inputdescription", { fileTypes: SUPPORTED_FILE_TYPES_STRING }),
      article: t("newtender.formgroups.document.inputs.other.article"),
      showOptional: true
    },
  ];

  const maxFilesError = form.formState.errors.document?.["maxerror"];

  return (
    <div id="document">
      {/* FormFields */}
      <div className="flex flex-col gap-4">
        {
          formFields.map((field, index) => (
            <FileUploadComponent
              key={index}
              form={form}
              name={field.name}
              label={field.label}
              article={field.article}
              description={field.description}
              showOptional={field.showOptional}
              accept={field.accept}
            />
          ))
        }
        {maxFilesError && (
          <Card className="bg-red-50 border-red-500 border-1 text-red-500 p-2 rounded-md">
            <p className="text-sm">{maxFilesError.message}</p>
          </Card>
        )}
      </div>
    </div>
  );
}
